import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Mail, MessageCircle, Loader2, CheckCircle2, Clock,
  Edit3, Check, X, Send, Play, ExternalLink, RefreshCw,
  Calendar, ChevronDown, ChevronUp, Zap
} from "lucide-react";
import { authFetch, API } from "../utils/api";

const EMAIL_STATUS_CONFIG = {
  pending: { label: "Pendiente", icon: <Clock size={11} />, cls: "text-amber-600 bg-amber-50" },
  sent:    { label: "Enviado",   icon: <CheckCircle2 size={11} />, cls: "text-green-700 bg-green-50" },
  failed:  { label: "Fallido",   icon: <X size={11} />, cls: "text-red-600 bg-red-50" },
  skipped: { label: "Omitido",   icon: <X size={11} />, cls: "text-gray-400 bg-gray-50" },
};

export default function EmailSequencePanel({ businessId, businessEmail, businessPhone, hasSite }) {
  const [seq, setSeq]               = useState(null);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving]   = useState(false);
  const [sendingIdx, setSendingIdx] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [editBuf, setEditBuf]       = useState({});
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [showWA, setShowWA]         = useState(false);
  const [editingWA, setEditingWA]   = useState(false);
  const [waBuf, setWaBuf]           = useState("");
  const [expanded, setExpanded]     = useState(true);

  useEffect(() => { load(); }, [businessId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/businesses/${businessId}/sequence`);
      if (res) {
        const d = await res.json();
        setSeq(Object.keys(d).length > 0 ? d : null);
      }
    } catch {}
    setLoading(false);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/businesses/${businessId}/sequence`, { method: "POST" });
      if (!res || !res.ok) throw new Error();
      setSeq(await res.json());
      toast.success("Secuencia de 3 emails generada con IA");
    } catch { toast.error("Error al generar la secuencia"); }
    finally { setGenerating(false); }
  };

  const saveEdit = async (idx) => {
    const emails = seq.emails.map(e => e.index === idx ? { ...e, ...editBuf } : e);
    const res = await authFetch(`${API}/businesses/${businessId}/sequence`, {
      method: "PUT",
      body: JSON.stringify({ emails }),
    });
    if (res?.ok) {
      setSeq(s => ({ ...s, emails }));
      toast.success("Email guardado");
    } else toast.error("Error al guardar");
    setEditingIdx(null);
  };

  const saveWA = async () => {
    const res = await authFetch(`${API}/businesses/${businessId}/sequence`, {
      method: "PUT",
      body: JSON.stringify({ whatsapp_message: waBuf }),
    });
    if (res?.ok) {
      setSeq(s => ({ ...s, whatsapp_message: waBuf }));
      toast.success("Mensaje WhatsApp guardado");
    } else toast.error("Error al guardar");
    setEditingWA(false);
  };

  const approve = async () => {
    setApproving(true);
    let scheduledStart = null;
    if (scheduleDate) {
      scheduledStart = `${scheduleDate}T${scheduleTime}:00`;
    } else {
      scheduledStart = new Date(Date.now() + 5 * 60 * 1000).toISOString();
    }
    try {
      const res = await authFetch(`${API}/businesses/${businessId}/sequence/approve`, {
        method: "POST",
        body: JSON.stringify({ scheduled_start: scheduledStart }),
      });
      if (!res?.ok) throw new Error();
      const d = await res.json();
      setSeq(s => ({ ...s, status: "active", emails: d.emails }));
      toast.success(scheduleDate ? `Secuencia activada — Email 1 el ${scheduleDate} a las ${scheduleTime}` : "Secuencia activada — Email 1 enviándose en 5 minutos");
    } catch { toast.error("Error al aprobar la secuencia"); }
    finally { setApproving(false); }
  };

  const sendNow = async (index) => {
    setSendingIdx(index);
    try {
      const res = await authFetch(`${API}/businesses/${businessId}/sequence/send/${index}`, { method: "POST" });
      if (!res?.ok) throw new Error((await res.json()).detail || "Error");
      toast.success(`Email #${index} enviado a ${businessEmail}`);
      load();
    } catch (e) { toast.error(e.message || "Error al enviar"); }
    finally { setSendingIdx(null); }
  };

  if (loading) return (
    <div className="flex justify-center py-6">
      <Loader2 className="animate-spin text-gray-300" size={20} />
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <Mail size={15} className={seq?.status === "active" ? "text-amber-500" : seq?.status === "completed" ? "text-green-500" : "text-gray-400"} />
          <div className="text-left">
            <p className="text-sm font-bold text-white">Secuencia de emails</p>
            <p className="text-xs text-gray-400">
              {!seq ? "Sin secuencia generada" :
               seq.status === "draft"     ? "Pendiente de aprobar" :
               seq.status === "active"    ? "Activa — enviando automáticamente" :
               seq.status === "completed" ? "Completada" : seq.status}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {expanded && (
        <>
          {!seq ? (
            /* Generate — only available when a site has been generated */
            !hasSite ? (
              <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900" data-testid="sequence-requires-site">
                <p className="font-bold mb-1">⚠️ Falta generar el sitio</p>
                <p>Los correos incluyen la URL del preview ({"{{preview}}"}). Generá el sitio web primero y después volvé acá.</p>
              </div>
            ) : (
              <button
                data-testid="generate-sequence-btn"
                onClick={generate}
                disabled={generating}
                className="w-full flex items-center justify-center gap-2 py-3 bg-[#0A0A0A] text-white rounded-xl text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {generating ? "Generando secuencia con IA..." : "Generar secuencia con IA"}
              </button>
            )
          ) : (
            <>
              {/* Email cards */}
              {seq.emails?.map(email => (
                <EmailCard
                  key={email.index}
                  email={email}
                  editing={editingIdx === email.index}
                  editBuf={editBuf}
                  onEdit={() => { setEditingIdx(email.index); setEditBuf({ subject: email.subject, body: email.body }); }}
                  onEditChange={f => v => setEditBuf(b => ({ ...b, [f]: v }))}
                  onSave={() => saveEdit(email.index)}
                  onCancel={() => setEditingIdx(null)}
                  onSendNow={() => sendNow(email.index)}
                  sending={sendingIdx === email.index}
                  seqStatus={seq.status}
                />
              ))}

              {/* Approve section */}
              {seq.status === "draft" && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-amber-800 mb-3">Programar y activar</p>
                  <div className="flex gap-2 mb-3">
                    <div className="flex-1">
                      <label className="text-[10px] text-gray-500 block mb-1">Fecha (opcional)</label>
                      <input
                        type="date"
                        value={scheduleDate}
                        onChange={e => setScheduleDate(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none"
                      />
                    </div>
                    <div className="w-24">
                      <label className="text-[10px] text-gray-500 block mb-1">Hora</label>
                      <input
                        type="time"
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none"
                      />
                    </div>
                  </div>
                  <p className="text-[10px] text-amber-700 mb-3">
                    Email 2 y 3 se enviarán automáticamente al día siguiente (adaptando la hora según cuándo se abra el anterior).
                  </p>
                  <button
                    data-testid="approve-sequence-btn"
                    onClick={approve}
                    disabled={approving}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
                  >
                    {approving ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    {approving ? "Activando..." : scheduleDate ? `Programar para ${scheduleDate}` : "Enviar primer email en 5 min"}
                  </button>
                </div>
              )}

              {/* Re-generate */}
              <button
                onClick={generate}
                disabled={generating}
                className="w-full flex items-center justify-center gap-1 py-2 text-xs text-gray-400 hover:text-gray-700 transition-colors"
              >
                <RefreshCw size={11} className={generating ? "animate-spin" : ""} />
                {generating ? "Regenerando..." : "Regenerar con IA"}
              </button>
            </>
          )}
        </>
      )}

      {/* WhatsApp message */}
      {seq && (
        <WhatsAppSection
          message={seq.whatsapp_message}
          link={seq.whatsapp_link}
          phone={businessPhone}
          editing={editingWA}
          buf={waBuf}
          showWA={showWA}
          onToggle={() => setShowWA(s => !s)}
          onEdit={() => { setEditingWA(true); setWaBuf(seq.whatsapp_message || ""); }}
          onBufChange={setWaBuf}
          onSave={saveWA}
          onCancel={() => setEditingWA(false)}
        />
      )}
    </div>
  );
}

function EmailCard({ email, editing, editBuf, onEdit, onEditChange, onSave, onCancel, onSendNow, sending, seqStatus }) {
  const cfg = EMAIL_STATUS_CONFIG[email.status] || EMAIL_STATUS_CONFIG.pending;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">
            {email.index}
          </span>
          <span className="text-xs font-semibold text-[#0A0A0A] truncate">
            {editing ? "Editando..." : email.subject}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className={`flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${cfg.cls}`}>
            {cfg.icon} {cfg.label}
          </span>
          {!editing && (
            <button
              data-testid={`edit-email-${email.index}`}
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-[#FF4F00] hover:bg-orange-50 rounded-lg transition-colors"
              title="Editar email"
            >
              <Edit3 size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3">
        {editing ? (
          <div className="space-y-2">
            <input
              value={editBuf.subject || ""}
              onChange={e => onEditChange("subject")(e.target.value)}
              placeholder="Asunto del email"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4F00]"
            />
            <textarea
              value={editBuf.body || ""}
              onChange={e => onEditChange("body")(e.target.value)}
              rows={10}
              placeholder="Cuerpo del email..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#FF4F00] resize-y font-mono leading-relaxed"
            />
            <div className="flex gap-2">
              <button onClick={onSave} className="flex-1 py-2 bg-[#0A0A0A] text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1">
                <Check size={12} /> Guardar cambios
              </button>
              <button onClick={onCancel} className="py-2 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold">
                <X size={12} />
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">
              {email.body}
            </p>
            {email.scheduled_at && email.status === "pending" && (
              <p className="text-[10px] text-gray-400 mt-2 flex items-center gap-1">
                <Calendar size={10} />
                Programado: {new Date(email.scheduled_at).toLocaleString("es-UY", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            {email.sent_at && (
              <p className="text-[10px] text-green-600 mt-2 flex items-center gap-1">
                <CheckCircle2 size={10} />
                Enviado: {new Date(email.sent_at).toLocaleString("es-UY", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
            {email.status === "pending" && seqStatus === "active" && (
              <button
                data-testid={`send-email-${email.index}-btn`}
                onClick={onSendNow}
                disabled={sending}
                className="mt-2 w-full py-2 flex items-center justify-center gap-1 text-[11px] font-semibold text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                {sending ? "Enviando..." : "Enviar ahora"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WhatsAppSection({ message, link, phone, editing, buf, showWA, onToggle, onEdit, onBufChange, onSave, onCancel }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <MessageCircle size={14} className="text-green-600" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Mensaje WhatsApp</span>
        </div>
        {showWA ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>

      {showWA && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3">
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={buf}
                onChange={e => onBufChange(e.target.value)}
                rows={5}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-gray-400 resize-none"
              />
              <div className="flex gap-2">
                <button onClick={onSave} className="flex-1 py-2 bg-[#0A0A0A] text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1">
                  <Check size={12} /> Guardar
                </button>
                <button onClick={onCancel} className="py-2 px-3 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold">
                  <X size={12} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed mb-3">{message}</p>
              <div className="flex gap-2">
                {link && (
                  <a
                    data-testid="whatsapp-send-link"
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-[#25D366] text-white rounded-xl text-xs font-semibold hover:bg-green-600 transition-colors"
                  >
                    <ExternalLink size={12} /> Abrir en WhatsApp
                  </a>
                )}
                <button
                  onClick={onEdit}
                  className="py-2.5 px-3 bg-gray-100 text-gray-600 rounded-xl text-xs font-semibold hover:bg-gray-200 transition-colors"
                >
                  <Edit3 size={12} />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
